import React from 'react'
import {
  Clipboard,
  ChevronRight,
  RefreshCw,
  ChevronUp,
  ChevronDown
} from 'lucide-react'
import Button from '../Button'
import NotificationButton from '../NotificationButton'
import { designTokens } from '@/utils/design-tokens'

export interface BreadcrumbItem {
  label: string
  icon?: React.ReactNode
  onClick?: () => void
}

export interface HeaderProps {
  breadcrumbs?: BreadcrumbItem[]
  onMyActivityClick?: () => void
  onNotificationClick?: () => void
  onRefreshClick?: () => void
  className?: string
}

const Header: React.FC<HeaderProps> = ({
  breadcrumbs = [
    { label: '프로젝트', icon: <Clipboard className="w-3 h-[15px]" strokeWidth={2} /> },
    { label: '사업관리' },
    { label: '사업생성' }
  ],
  onMyActivityClick,
  onNotificationClick,
  onRefreshClick,
  className = ''
}) => {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleScrollToBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
  }

  return (
    <header className={`bg-white border-b-2 ${className}`} style={{ borderBottomColor: designTokens.colors.gray[1] }}>
      <div className="flex justify-between items-center gap-2 sm:gap-[10px] px-3 sm:px-5 h-[70px]">
        {/* Left Side - Page Title + Parent Breadcrumb */}
        <div className="flex items-baseline gap-3 min-w-0 flex-1">
          {breadcrumbs.length > 0 && (
            <>
              {/* Page Title (last breadcrumb item) - large, prominent */}
              <h1
                className="whitespace-nowrap truncate"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['title-medium'][0],
                  lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                  fontWeight: 700,
                  color: designTokens.colors.gray[6]
                }}
              >
                {breadcrumbs[breadcrumbs.length - 1].label}
              </h1>

              {/* Parent breadcrumb path - small, secondary (hidden on mobile) */}
              {breadcrumbs.length > 1 && (
                <nav className="hidden sm:flex items-center gap-[3px] min-w-0">
                  {breadcrumbs.slice(0, -1).map((item, index, arr) => (
                    <React.Fragment key={index}>
                      <div className="flex items-center gap-[5px] min-w-0">
                        {item.icon && (
                          <div
                            className="w-[14px] h-[14px] flex items-center justify-center flex-shrink-0"
                            style={{ color: designTokens.colors.gray[5] }}
                          >
                            {item.icon}
                          </div>
                        )}
                        <button
                          onClick={item.onClick}
                          className="transition-colors truncate"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-small'][0],
                            lineHeight: designTokens.typography.fontSize['body-small'][1].lineHeight,
                            fontWeight: 400,
                            color: designTokens.colors.gray[5]
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = designTokens.colors.gray[6]
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = designTokens.colors.gray[5]
                          }}
                        >
                          {item.label}
                        </button>
                      </div>
                      {index < arr.length - 1 && (
                        <ChevronRight
                          className="w-3 h-3 flex-shrink-0 mx-[3px]"
                          strokeWidth={2}
                          style={{ color: designTokens.colors.gray[5] }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </nav>
              )}
            </>
          )}
        </div>

        {/* Right Side - Action Buttons */}
        <div className="flex items-center gap-1 sm:gap-[10px] flex-shrink-0">
          {/* Notification Button */}
          <NotificationButton />

          {/* Scroll to Top Button */}
          <Button
            size="medium"
            color="white"
            onClick={handleScrollToTop}
            className="h-10 px-2 sm:px-[10px] border-2 cursor-pointer"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="최상단으로 이동"
          >
            <ChevronUp className="w-[14.25px] h-[13.5px]" strokeWidth={2} />
          </Button>

          {/* Scroll to Bottom Button */}
          <Button
            size="medium"
            color="white"
            onClick={handleScrollToBottom}
            className="h-10 px-2 sm:px-[10px] border-2 cursor-pointer"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="최하단으로 이동"
          >
            <ChevronDown className="w-[14.25px] h-[13.5px]" strokeWidth={2} />
          </Button>

          {/* Refresh Button */}
          <Button
            size="medium"
            color="white"
            onClick={onRefreshClick}
            className="h-10 px-2 sm:px-[10px] border-2 cursor-pointer"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="새로고침"
          >
            <RefreshCw className="w-[14.25px] h-[13.5px]" strokeWidth={2} />
          </Button>
        </div>
      </div>
    </header>
  )
}

export default Header